import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Image as ImageIcon, Send, Ban } from "lucide-react";
import { api, apiErr, Badge, inputCls, labelCls } from "./adminShared";

const STATUSES = ["submitted", "photo_review", "more_photos_needed", "quote_sent", "accepted", "pending_deposit", "confirmed", "declined"];

function Photo({ path }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let rev; api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); rev = u; setUrl(u); }).catch(() => {});
    return () => rev && URL.revokeObjectURL(rev);
  }, [path]);
  if (!url) return <div className="w-full h-20 bg-[#F5F0E8] rounded-lg flex items-center justify-center"><ImageIcon className="text-[#B87333]/40" /></div>;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" className="w-full h-20 object-cover rounded-lg border border-[#E6DEC3]" /></a>;
}

function QuoteModal({ e, onClose, onChanged }) {
  const [f, setF] = useState({
    status: e.status, final_price: e.final_price ?? "", owner_note: e.owner_note ?? "",
    quote_duration_minutes: e.quote_duration_minutes ?? 240,
    included: (e.included_services || []).join(", "),
    expires_at: e.expires_at ? e.expires_at.slice(0, 10) : "",
  });
  const [optional, setOptional] = useState(e.optional_items || []);

  const save = async (extra = {}) => {
    try {
      const payload = {
        status: f.status, owner_note: f.owner_note,
        quote_duration_minutes: Number(f.quote_duration_minutes) || 240,
        included_services: f.included.split(",").map((x) => x.trim()).filter(Boolean),
        optional_items: optional,
        ...extra,
      };
      if (f.final_price !== "") payload.final_price = Number(f.final_price);
      if (f.expires_at) payload.expires_at = new Date(f.expires_at + "T23:59:59Z").toISOString();
      await api.patch(`/admin/estimates/${e.estimate_id}`, payload);
      toast.success("Quote saved"); onChanged();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const sendQuote = async () => {
    if (f.final_price === "") { toast.error("Enter a final price first"); return; }
    await save();
    try { const { data } = await api.post(`/admin/estimates/${e.estimate_id}/send-quote`); toast.success("Quote emailed to customer"); onChanged(); onClose(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const requestPhotos = () => save({ status: "more_photos_needed" }).then(() => { toast.message("Marked: more photos needed"); onClose(); });
  const decline = () => save({ status: "declined" }).then(() => onClose());

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="estimate-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex justify-between items-start">
          <div><p className="text-xs uppercase tracking-[0.15em] text-[#B87333] font-semibold">{e.estimate_number}</p><h3 className="font-serif text-2xl text-[#2C2621] mt-1">Quote request</h3></div>
          <button onClick={onClose}><X className="text-[#635B52]" /></button>
        </div>
        <div className="mt-3 text-sm text-[#635B52] space-y-1">
          <p><strong className="text-[#2C2621]">Customer:</strong> {e.name} · {e.email} · {e.phone}</p>
          <p><strong className="text-[#2C2621]">Address:</strong> {e.address}</p>
          <p><strong className="text-[#2C2621]">Property:</strong> {e.property_type} · {e.bedrooms} bd / {e.bathrooms} ba · {e.requested_service}</p>
          {e.preferred_date && <p><strong className="text-[#2C2621]">Preferred date:</strong> {e.preferred_date}</p>}
          {e.details && <p><strong className="text-[#2C2621]">Notes:</strong> {e.details}</p>}
          {e.booking_number && <p><strong className="text-[#2C2621]">Booking:</strong> {e.booking_number}</p>}
        </div>
        {(e.photos?.length > 0 || e.photo_paths?.length > 0) && (
          <div className="mt-4"><p className={labelCls}>Photos</p>
            <div className="mt-2 grid grid-cols-3 gap-2" data-testid="estimate-photos">
              {(e.photos || e.photo_paths.map((p) => ({ path: p, label: "" }))).map((p, i) => (
                <div key={i} className="relative"><Photo path={p.path || p} />{p.label && <span className="absolute top-1 left-1 text-[9px] bg-[#2C2621] text-white px-1 rounded">{p.label.split(" ")[0]}</span>}</div>
              ))}
            </div>
          </div>
        )}
        <div className="mt-5 border-t border-[#E6DEC3] pt-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><label className={labelCls}>Status</label><select value={f.status} onChange={(ev) => setF({ ...f, status: ev.target.value })} className={inputCls} data-testid="est-status">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div><label className={labelCls}>Final price ($)</label><input type="number" value={f.final_price} onChange={(ev) => setF({ ...f, final_price: ev.target.value })} className={inputCls} data-testid="est-quote" /></div>
            <div><label className={labelCls}>Duration (min)</label><input type="number" value={f.quote_duration_minutes} onChange={(ev) => setF({ ...f, quote_duration_minutes: ev.target.value })} className={inputCls} data-testid="est-duration" /></div>
            <div><label className={labelCls}>Expires</label><input type="date" value={f.expires_at} onChange={(ev) => setF({ ...f, expires_at: ev.target.value })} className={inputCls} data-testid="est-expires" /></div>
          </div>
          <div><label className={labelCls}>Included services (comma separated)</label><input value={f.included} onChange={(ev) => setF({ ...f, included: ev.target.value })} className={inputCls} data-testid="est-included" /></div>
          <div><label className={labelCls}>Owner note to customer</label><textarea rows={2} value={f.owner_note} onChange={(ev) => setF({ ...f, owner_note: ev.target.value })} className={inputCls} data-testid="est-note" /></div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => save()} className="bg-[#B87333] text-white rounded-full px-4 py-2 text-sm" data-testid="est-save">Save</button>
            <button onClick={sendQuote} className="inline-flex items-center gap-1 bg-[#2C2621] text-white rounded-full px-4 py-2 text-sm" data-testid="est-send"><Send size={13} /> Send quote</button>
            <button onClick={requestPhotos} className="border border-[#B87333]/40 text-[#2C2621] rounded-full px-4 py-2 text-sm" data-testid="est-morephotos">Request more photos</button>
            <button onClick={decline} className="inline-flex items-center gap-1 border border-red-200 text-red-600 rounded-full px-4 py-2 text-sm" data-testid="est-decline"><Ban size={13} /> Decline</button>
          </div>
          <p className="text-xs text-[#635B52]">Quotes expire 7 days after sending unless you set a later date. When the customer accepts, they pay a 50% deposit and the booking becomes Confirmed after Stripe processes it.</p>
        </div>
      </div>
    </div>
  );
}

export default function AdminEstimates() {
  const [estimates, setEstimates] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get("/admin/estimates").then((r) => setEstimates(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {estimates.map((e) => (
          <button key={e.estimate_id} onClick={() => setSel(e)} className="text-left bg-white rounded-2xl border border-[#E6DEC3] p-5 hover:border-[#B87333]/50 transition-all" data-testid={`estimate-item-${e.estimate_number}`}>
            <div className="flex justify-between items-start"><p className="text-xs uppercase tracking-[0.15em] text-[#B87333] font-semibold">{e.estimate_number}</p><Badge s={e.status} /></div>
            <p className="mt-2 font-medium text-[#2C2621]">{e.name}</p>
            <p className="text-sm text-[#635B52]">{e.address}</p>
            <p className="mt-2 text-xs text-[#635B52]">{(e.photos?.length ?? e.photo_paths?.length ?? 0)} photo(s){e.final_price ? ` · $${Number(e.final_price).toFixed(2)}` : ""}</p>
          </button>
        ))}
        {!estimates.length && <p className="text-[#635B52] col-span-full py-10 text-center">No quote requests yet.</p>}
      </div>
      {sel && <QuoteModal e={sel} onClose={() => setSel(null)} onChanged={load} />}
    </div>
  );
}
